import React, { useEffect, useState, useRef, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, Package, ShoppingCart, Users, Settings, 
    LogOut, Menu, Layers, FileText, List, Home, Cpu, CreditCard,
    MessageSquare, History // 👉 ĐÃ THÊM: Import icon History
} from 'lucide-react';
import { toast } from 'react-toastify';
import chatService from '../services/chatService';
import { adminStaffService } from '../services/adminStaffService';
import { getImageUrl } from '../utils/helpers';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const LogoImg = '/logo.png';

    const userStr = localStorage.getItem('user_info');
    const user = userStr ? JSON.parse(userStr) : null;
    
    const rawRole = user?.role?.roleName || user?.role || '';
    const role = rawRole.toUpperCase();
    
    const isAdmin = role === 'ROLE_ADMIN' || role === 'ADMIN';
    const isStaff = role === 'ROLE_STAFF' || role === 'STAFF';

    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [pendingOrderCount, setPendingOrderCount] = useState(0);
    const isInitialOrderFetch = useRef(true); 

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        if (!user || (!isAdmin && !isStaff)) {
            navigate('/');
            return;
        }
        if (isStaff && location.pathname === '/admin') {
            navigate('/admin/orders');
        }
    }, [user, isAdmin, isStaff, location.pathname, navigate]);

    const fetchAndCountUnread = async () => {
        try {
            const res = await chatService.getActiveRooms();
            const roomList = Array.isArray(res) ? res : (res?.data || []);
            const unreadCount = roomList.filter(room => room.unreadCount > 0).length;
            setUnreadChatCount(unreadCount);
        } catch (error) {
            console.error("Lỗi đếm số phòng chưa đọc:", error);
        }
    };

    const fetchPendingOrders = useCallback(async () => {
        try {
            const res = await adminStaffService.getAllOrders();
            const orderList = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            
            const pendingOrders = orderList.filter(o => o.orderStatus === 0);
            
            setPendingOrderCount(prev => {
                if (!isInitialOrderFetch.current && pendingOrders.length > prev) {
                    toast.success(
                        <div className="flex flex-col cursor-pointer" onClick={() => navigate('/admin/orders')}>
                            <span className="font-bold text-sm">Đơn hàng mới!</span>
                            <span className="text-xs mt-1">Đang có {pendingOrders.length} đơn chờ xác nhận.</span>
                        </div>,
                        { icon: "🛒", autoClose: 5000, theme: "colored" }
                    );
                }
                isInitialOrderFetch.current = false;
                return pendingOrders.length;
            });
        } catch (error) {
            console.error("Lỗi đếm đơn hàng chờ:", error);
        }
    }, [navigate]);

    useEffect(() => {
        if (!isAdmin && !isStaff) return;

        fetchAndCountUnread();
        fetchPendingOrders();

        const orderInterval = setInterval(fetchPendingOrders, 30000);

        let isMounted = true; 
        let unsubscribeNotif = null;

        chatService.connect(
            () => {
                if (isMounted) {
                    unsubscribeNotif = chatService.subscribeToStaffNotifications((message) => {
                        if (message.senderType !== 'STAFF') {
                            fetchAndCountUnread();
                            
                            toast.info(
                                <div className="flex flex-col cursor-pointer" onClick={() => navigate('/admin/chat')}>
                                    <span className="font-bold text-sm text-gray-800">{message.senderName}</span>
                                    <span className="text-xs text-gray-600 truncate mt-1">{message.content}</span>
                                </div>,
                                { 
                                    icon: "💬", 
                                    autoClose: 5000, 
                                    theme: "light",
                                    toastId: `msg-${message.messageId}` 
                                }
                            );
                        }
                    });
                }
            },
            (error) => console.error("Lỗi kết nối WS Chat:", error)
        );

        const handleRoomRead = () => fetchAndCountUnread();
        window.addEventListener('chatRoomRead', handleRoomRead);

        return () => {
            isMounted = false; 
            clearInterval(orderInterval);
            if (unsubscribeNotif) unsubscribeNotif();
            window.removeEventListener('chatRoomRead', handleRoomRead);
        };
    }, [isAdmin, isStaff, navigate, fetchPendingOrders]);

    useEffect(() => {
        const totalNotifications = unreadChatCount + pendingOrderCount;
        
        if (totalNotifications > 0) {
            document.title = `(${totalNotifications}) Quản trị | Thành Nam PC`;
        } else {
            document.title = 'Quản trị | Thành Nam PC';
        }
        
        return () => { document.title = 'Thành Nam PC - Hệ thống máy tính'; };
    }, [unreadChatCount, pendingOrderCount]);

    const handleLogout = () => {
        chatService.disconnect();
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_info');
        window.location.href = '/';
    };

    const allSidebarItems = [
        { name: 'Tổng quan', path: '/admin', icon: <LayoutDashboard size={20} />, end: true, roles: ['ROLE_ADMIN', 'ADMIN'] },
        { name: 'Tin nhắn CSKH', path: '/admin/chat', icon: <MessageSquare size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Đơn hàng', path: '/admin/orders', icon: <ShoppingCart size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Sản phẩm', path: '/admin/products', icon: <Package size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Thuộc tính SP', path: '/admin/attributes', icon: <Layers size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Tài khoản', path: '/admin/accounts', icon: <Users size={20} />, roles: ['ROLE_ADMIN', 'ADMIN'] },
        { name: 'Menu điều hướng', path: '/admin/menus', icon: <List size={20} />, roles: ['ROLE_ADMIN', 'ADMIN'] },
        { name: 'PT Thanh toán', path: '/admin/payments', icon: <CreditCard size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Tin tức & Banner', path: '/admin/content', icon: <FileText size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Cấu hình Build PC', path: '/admin/pc-builder-config', icon: <Cpu size={20} />, roles: ['ROLE_ADMIN', 'ADMIN', 'ROLE_STAFF', 'STAFF'] },
        { name: 'Nhật ký hoạt động', path: '/admin/action-logs', icon: <History size={20} />, roles: ['ROLE_ADMIN', 'ADMIN'] }, // 👉 ĐÃ THÊM MỤC NÀY
        { name: 'Cài đặt chung', path: '/admin/settings', icon: <Settings size={20} />, roles: ['ROLE_ADMIN', 'ADMIN'] },
    ];

    const sidebarItems = allSidebarItems.filter(item => item.roles.includes(role));

    const getRoleName = () => {
        if (isAdmin) return 'Quản trị viên';
        if (isStaff) return 'Nhân viên';
        return 'Thành viên';
    };

    return (
        <div className="flex h-screen bg-gray-50 font-inter relative">
            
            {isMobileMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col shadow-2xl md:shadow-sm transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 shrink-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <Link to="/" className="h-20 flex items-center justify-center border-b border-gray-100 shrink-0">
                    <img src={LogoImg} alt="Thành Nam PC" className="h-16 object-contain" onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(null); }} />
                </Link>
                
                <nav className="flex-1 overflow-y-auto py-6 px-3 scrollbar-thin">
                    <ul className="space-y-2">
                        {sidebarItems.map((item, index) => (
                            <li key={index}>
                                <NavLink 
                                    to={item.path}
                                    end={item.end}
                                    onClick={() => setIsMobileMenuOpen(false)} 
                                    className={({ isActive }) => 
                                        `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                                            isActive 
                                            ? 'bg-tnc-orange text-white shadow-md shadow-orange-200' 
                                            : 'text-gray-600 hover:bg-orange-50 hover:text-tnc-orange' 
                                        }`
                                    }
                                >
                                    <div className="relative">
                                        {item.icon}
                                        {item.path === '/admin/chat' && unreadChatCount > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                                        )}
                                        {item.path === '/admin/orders' && pendingOrderCount > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                                        )}
                                    </div>
                                    {item.name}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="p-4 border-t border-gray-100 flex flex-col gap-2 shrink-0">
                    <Link 
                        to="/"
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-tnc-orange bg-orange-50 hover:bg-tnc-orange hover:text-white font-bold rounded-xl transition-colors border border-orange-100"
                    >
                        <Home size={18} />
                        VỀ TRANG CHỦ
                    </Link>
                    
                    <button 
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-gray-600 hover:text-red-600 hover:bg-red-50 font-bold rounded-xl transition-colors"
                    >
                        <LogOut size={18} />
                        ĐĂNG XUẤT
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="h-20 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-8 z-10 shrink-0">
                    
                    <button 
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="md:hidden text-gray-600 hover:text-tnc-orange transition-colors p-2 -ml-2"
                    >
                        <Menu size={26} />
                    </button>
                    
                    <div className="flex-1"></div>
                    
                    <div className="flex items-center gap-4 sm:gap-6">
                        
                        <Link to="/admin/chat" className="relative text-gray-400 hover:text-tnc-orange transition-colors">
                            <MessageSquare size={24} />
                            {unreadChatCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white shadow-sm animate-bounce">
                                    {unreadChatCount > 9 ? '9+' : unreadChatCount}
                                </span>
                            )}
                        </Link>

                        <Link to="/admin/orders" className="relative text-gray-400 hover:text-tnc-orange transition-colors ml-1 sm:ml-2">
                            <ShoppingCart size={24} />
                            {pendingOrderCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white shadow-sm animate-bounce">
                                    {pendingOrderCount > 9 ? '9+' : pendingOrderCount}
                                </span>
                            )}
                        </Link>

                        <div className="w-px h-8 bg-gray-200 ml-1 sm:ml-2"></div>

                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-bold text-gray-800">{getRoleName()}</p>
                                <div className="flex items-center justify-end gap-1.5 mt-0.5">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    <p className="text-xs text-gray-500">{user?.accountName || user?.username || 'Đang hoạt động'}</p>
                                </div>
                            </div>
                            <div className="w-11 h-11 bg-orange-50 border-2 border-tnc-orange rounded-full flex items-center justify-center text-tnc-orange font-bold shadow-sm uppercase">
                                {user?.accountName ? user.accountName.charAt(0) : (user?.username ? user.username.charAt(0) : 'U')}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50/50">
                    <Outlet /> 
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;